import React, { useMemo, useState } from "react";
import {
  SectionCard,
  StatusBadge,
  EmptyState,
  Pagination,
} from "../../../components/panel/PanelUI";

export default function ContractsView({
  contracts,
  statusFilter,
  onStatusFilterChange,
  page,
  totalPages,
  onPageChange,
  onView,
  onDelete,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [openMenuId, setOpenMenuId] = useState(null);

  const getBuyerName = (contract) =>
    [contract.buyer?.firstName, contract.buyer?.lastName]
      .filter(Boolean)
      .join(" ");

  const filteredContracts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const list = Array.isArray(contracts) ? contracts : [];

    return list
      .filter((contract) => {
        if (!normalizedSearch) return true;

        const buyerName = getBuyerName(contract);

        const searchableText = [
          contract.vehicle?.name,
          contract.plateNumber,
          buyerName,
          contract.seller?.name,
          contract.status,
          contract.createdAt
            ? new Date(contract.createdAt).toLocaleDateString("fa-IR")
            : "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(normalizedSearch);
      })
      .sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return sortOrder === "newest" ? bTime - aTime : aTime - bTime;
      });
  }, [contracts, searchTerm, sortOrder]);

  const hasContracts = Array.isArray(contracts) && contracts.length > 0;
  const hasActiveFilters = Boolean(searchTerm.trim() || statusFilter);

  return (
    <SectionCard title="قولنامه‌های قبلی">
      {!hasContracts && !hasActiveFilters ? (
        <EmptyState
          icon="📄"
          title="هنوز قولنامه‌ای ثبت نکرده‌اید"
          description="قولنامه‌های خرید و فروشی که ثبت می‌کنید، اینجا لیست می‌شود."
        />
      ) : (
        <>
          <div className="mb-4 grid gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 sm:grid-cols-[1fr_160px]">
            <label className="relative block">
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                <SearchIcon />
              </span>
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="جستجو بر اساس خودرو، پلاک، خریدار یا فروشنده"
                className="h-11 w-full rounded-xl border border-gray-200 bg-white pr-10 pl-3 text-sm font-semibold text-navy outline-none transition placeholder:text-xs placeholder:font-medium placeholder:text-gray-400 focus:border-sky"
              />
            </label>

            <select
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
              className="h-11 rounded-xl border border-gray-200 bg-white px-3 text-xs font-bold text-navy outline-none transition focus:border-sky"
            >
              <option value="newest">جدیدترین</option>
              <option value="oldest">قدیمی‌ترین</option>
            </select>

            <div className="flex flex-wrap gap-2 sm:col-span-2">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value || "all"}
                  type="button"
                  onClick={() => onStatusFilterChange(filter.value)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition ${
                    statusFilter === filter.value
                      ? "bg-navy text-white"
                      : "bg-white text-gray-500 ring-1 ring-gray-200 hover:text-navy"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    onStatusFilterChange("");
                  }}
                  className="rounded-full px-3 py-1.5 text-[11px] font-bold text-red-600 transition hover:bg-red-50"
                >
                  پاک کردن فیلترها
                </button>
              )}
            </div>
          </div>

          {filteredContracts.length === 0 ? (
            <EmptyState
              icon="🔎"
              title="نتیجه‌ای پیدا نشد"
              description="عبارت جستجو یا فیلتر وضعیت را تغییر دهید."
            />
          ) : (
            <div className="flex flex-col gap-3">
              {filteredContracts.map((c) => {
                const isCompleted = c.status === "completed";
                const buyerName = getBuyerName(c) || "خریدار نامشخص";

                return (
                  <div
                    key={c.id || c._id}
                    className={`flex items-center justify-between gap-3 rounded-xl border p-4 text-right transition ${
                      isCompleted
                        ? "border-gray-100 hover:border-sky hover:bg-sky/5"
                        : "border-gray-100 bg-gray-50"
                    }`}
                  >
                    <div
                      className={`min-w-0 flex-1 ${isCompleted ? "cursor-pointer" : "opacity-75"}`}
                      onClick={() =>
                        isCompleted && onView && onView(c.id || c._id)
                      }
                      title={
                        isCompleted
                          ? undefined
                          : "مشاهده قولنامه فقط بعد از تکمیل شدن امکان‌پذیر است"
                      }
                    >
                      <p className="truncate text-sm font-bold text-navy">
                        {c.vehicle?.name || "خودرو نامشخص"}
                        {c.plateNumber ? ` — ${c.plateNumber}` : ""}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {c.createdAt
                          ? new Date(c.createdAt).toLocaleDateString("fa-IR")
                          : "—"}
                      </p>
                      <div>
                        <p className="mt-1 text-xs text-gray-400">
                          {buyerName}{" "}
                          {c.seller?.name ? `— ${c.seller.name}` : ""}
                        </p>
                        {!isCompleted && (
                          <p className="mt-1 text-xs font-bold text-gray-400">
                            پس از تکمیل قابل مشاهده است
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 relative">
                      <StatusBadge status={c.status} />

                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(
                              openMenuId === (c.id || c._id)
                                ? null
                                : c.id || c._id,
                            );
                          }}
                          className="h-8 w-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
                          aria-haspopup="menu"
                          aria-expanded={openMenuId === (c.id || c._id)}
                        >
                          <ThreeDotsIcon />
                        </button>

                        {openMenuId === (c.id || c._id) && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="absolute left-0 top-10 z-10 min-w-[140px] rounded-lg border bg-white p-1 shadow-lg"
                          >
                            {!isCompleted && onDelete && (
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  onDelete(c.id || c._id);
                                }}
                                className="w-full rounded-md px-3 py-2 text-right text-sm font-bold text-red-600 hover:bg-red-50"
                              >
                                حذف
                              </button>
                            )}
                            {isCompleted && (
                              <div className="px-3 py-2 text-xs text-gray-400">
                                عملیاتی موجود نیست
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={onPageChange}
          />
        </>
      )}
    </SectionCard>
  );
}

const STATUS_FILTERS = [
  { value: "", label: "همه" },
  { value: "completed", label: "تکمیل‌شده" },
  { value: "pending_buyer", label: "در انتظار خریدار" },
  { value: "pending_seller", label: "در انتظار فروشنده" },
  { value: "draft", label: "پیش‌نویس" },
  { value: "cancelled", label: "لغو شده" },
];

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="m21 21-4.3-4.3M10.8 18a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ThreeDotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="5" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="19" cy="12" r="1.6" fill="currentColor" />
    </svg>
  );
}
